import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * GLOBAL scroll-reset for VelvetGate route navigation.
 *
 * Mounted once at the top of the router tree (inside <BrowserRouter> but
 * above <Routes>). On every full-page route change (pathname change) we
 * scroll the window back to the top so hosts never land midway down a
 * fresh page.
 *
 * Scope — intentionally narrow:
 *   ✓ Triggers ONLY on `location.pathname` change.
 *   ✓ Hash changes (`#section`) are IGNORED so in-page anchor links keep
 *     working normally.
 *   ✓ Query-string-only changes (`?tab=live`) are IGNORED so future
 *     tab-deep-links don't yank the page to the top on every filter change.
 *   ✓ Modals, dropdowns, sheets, and any container with its own
 *     `overflow-auto`/`overflow-scroll` are unaffected — we touch
 *     `window` only, not arbitrary scroll containers.
 *
 * Mobile-first:
 *   - `behavior: 'auto'` (not 'smooth') so we never animate during a route
 *     transition. Smooth-scrolling on entry creates visible flicker and
 *     interacts badly with sticky headers on iOS Safari.
 *   - `top: 0, left: 0` keeps any horizontal overflow at the left edge.
 *
 * Browser back/forward:
 *   - This component does NOT call `history.scrollRestoration = 'manual'`.
 *     React Router v6 already turns the browser's native restoration off
 *     for SPA history entries, so back/forward navigation gets the same
 *     "land at top" behavior — which the spec calls for. The browser's
 *     native restoration is preserved for full document refresh.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Some browsers (notably iOS Safari) don't honor `window.scrollTo(0,0)`
    // when called synchronously inside a layout-effect race. Wrap in
    // requestAnimationFrame so the scroll happens AFTER the new route's
    // first paint commits.
    const id = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [pathname]);

  return null;
}
